/**
 * Department Recent Activity list used by Custodian and Coordinator dashboards.
 * Summary cards + dense activity rows with navigation to complaint or device detail.
 */

import React, { useState } from 'react';
import { DeviceIDBadge, ComplaintIDBadge } from '../../../../shared/components/common/Badges';
import StatsCard from '../../../../shared/components/common/StatsCard';
import ComplaintView from '../../../complaints/components/ComplaintView';
import MaintenanceLogView from '../../../devices/components/MaintenanceLogView';
import {
  getActivityDescriptionParts,
  resolveDepartmentActivitySummary,
} from '../../pages/recentActivityHelpers';

const getMaintenanceTypeLabel = (maintenanceType) => {
  const labelMap = {
    ppm: 'PPM',
    calibration: 'CAL',
    compliance: 'COM',
  };
  return labelMap[maintenanceType?.toLowerCase()] || maintenanceType;
};

const formatActivityTime = (timestamp) => {
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const DepartmentRecentActivity = ({
  recentActivity = [],
  summary = null,
  token,
  user,
  onNavigateToDeviceView,
  onNavigateToMaintenanceHistory,
  onNavigateToActivityLog,
  scopeNote = "this department's activity log",
}) => {
  const [selectedComplaintForModal, setSelectedComplaintForModal] = useState(null);
  const [selectedMaintenanceForModal, setSelectedMaintenanceForModal] = useState(null);
  const PREVIEW_LIMIT = 10;

  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const formatDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const dateRange = `${formatDate(sevenDaysAgo)} - ${formatDate(today)}`;
  const stats = resolveDepartmentActivitySummary(summary, recentActivity);
  const totalInWindow = stats.total ?? recentActivity.length;
  const visibleRows = recentActivity.slice(0, PREVIEW_LIMIT);

  const openComplaint = (act) => {
    if (!act?.complaint_id) return;
    setSelectedComplaintForModal({ id: act.complaint_id });
  };

  const openDevice = (act) => {
    if (!act?.device_id || typeof onNavigateToDeviceView !== 'function') return;
    onNavigateToDeviceView(act.device_id);
  };

  const openMaintenance = (act) => {
    if (act?.maintenance_log_id) {
      setSelectedMaintenanceForModal({ id: act.maintenance_log_id });
      return;
    }
    const deviceIdentifier = act?.device_identifier || act?.entity_id;
    if (deviceIdentifier && typeof onNavigateToMaintenanceHistory === 'function') {
      onNavigateToMaintenanceHistory({ device_identifier: deviceIdentifier });
    }
  };

  return (
    <div>
      <div className="list-default-window-note" role="note">
        <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
        <span>
          Card counts are the full 7 days from {scopeNote}, <strong>{dateRange}</strong>.
          The list shows up to the 10 newest.
        </span>
      </div>
      <div className="dashboard-stats-summary" style={{ marginBottom: '1rem' }}>
        <StatsCard
          icon="📋"
          number={stats.complaints_opened}
          label="Complaints Opened"
          className="open"
        />
        <StatsCard
          icon="✅"
          number={stats.complaints_closed}
          label="Complaints Closed"
          className="closed"
        />
        <StatsCard
          icon="❌"
          number={stats.complaints_cancelled}
          label="Complaints Cancelled"
          className="cancelled"
        />
        <StatsCard
          icon="📅"
          number={stats.maintenance_by_type.ppm}
          label="PPM"
          className="pending"
        />
        <StatsCard
          icon="⚙️"
          number={stats.maintenance_by_type.calibration}
          label="Calibration"
          className="calibration"
        />
        <StatsCard
          icon="✅"
          number={stats.maintenance_by_type.compliance}
          label="Compliance"
          className="active"
        />
      </div>

      <h3 className="dashboard-activity-list-heading">Most recent</h3>
      <div className="dashboard-activity-list">
        {visibleRows.map((act, idx) => {
          const isComplaint = act.activity_type === 'complaint_created'
            || act.activity_type === 'complaint_closed'
            || act.activity_type === 'complaint_cancelled';
          const isMaintenance = act.activity_type === 'maintenance_logged';
          const deviceIdentifier = act.device_identifier || (isComplaint ? null : act.entity_id);
          const canOpenComplaint = Boolean(act.complaint_id);
          const canOpenDevice = Boolean(act.device_id) && typeof onNavigateToDeviceView === 'function';
          const canOpenMaintenance = isMaintenance && (
            Boolean(act.maintenance_log_id)
            || (Boolean(deviceIdentifier) && typeof onNavigateToMaintenanceHistory === 'function')
          );
          const rowOpensComplaint = isComplaint && canOpenComplaint;
          const rowOpensMaintenance = isMaintenance && canOpenMaintenance;
          const rowClickable = rowOpensComplaint || rowOpensMaintenance;
          const { label, rest } = getActivityDescriptionParts(act);

          const handleRowClick = () => {
            if (rowOpensComplaint) openComplaint(act);
            else if (rowOpensMaintenance) openMaintenance(act);
          };

          return (
            <div
              key={`${act.activity_type}-${act.entity_id}-${act.timestamp}-${idx}`}
              className={`dashboard-activity-row${isComplaint ? ' is-complaint' : isMaintenance ? ' is-maintenance' : ''}${rowClickable ? ' is-clickable' : ''}`}
              onClick={rowClickable ? handleRowClick : undefined}
              title={rowOpensComplaint ? 'View complaint details' : rowOpensMaintenance ? 'View maintenance details' : undefined}
            >
              <div className="dashboard-activity-row__main">
                {isMaintenance && act.maintenance_type && (
                  <span
                    className="maintenance-type-badge"
                    data-type={act.maintenance_type?.toLowerCase()}
                  >
                    {getMaintenanceTypeLabel(act.maintenance_type)}
                  </span>
                )}
                {isComplaint && act.entity_id && (
                  <ComplaintIDBadge
                    complaintId={act.entity_id}
                    size="small"
                    clickable={canOpenComplaint}
                    title={canOpenComplaint ? 'View complaint details' : undefined}
                    onClick={canOpenComplaint ? () => openComplaint(act) : undefined}
                  />
                )}
                {deviceIdentifier && (
                  <DeviceIDBadge
                    deviceId={deviceIdentifier}
                    size="small"
                    clickable={canOpenDevice}
                    title={canOpenDevice ? 'View device details' : undefined}
                    onClick={canOpenDevice ? () => openDevice(act) : undefined}
                  />
                )}
                <span className="dashboard-activity-row__desc">
                  {label ? (
                    <>
                      <strong className="dashboard-activity-row__action">{label}</strong>
                      {rest ? `: ${rest}` : ''}
                    </>
                  ) : (
                    rest
                  )}
                </span>
              </div>
              <div className="dashboard-activity-row__meta">
                <span title={act.actor_employee_id || undefined}>{act.actor_name || 'Unknown'}</span>
                <span>•</span>
                <span>{formatActivityTime(act.timestamp)}</span>
              </div>
            </div>
          );
        })}
      </div>
      {(totalInWindow > visibleRows.length || (totalInWindow > 0 && typeof onNavigateToActivityLog === 'function')) && (
        <div className="dashboard-activity-list-footer">
          {totalInWindow > visibleRows.length && (
            <span>
              {visibleRows.length} of {totalInWindow} in this 7-day window
            </span>
          )}
          {typeof onNavigateToActivityLog === 'function' && (
            <button
              type="button"
              className="link-button dashboard-activity-view-all"
              onClick={() => onNavigateToActivityLog()}
            >
              View activity log
            </button>
          )}
        </div>
      )}

      {selectedComplaintForModal && (
        <ComplaintView
          complaintId={selectedComplaintForModal.id}
          token={token}
          onClose={() => setSelectedComplaintForModal(null)}
          onUpdate={() => {}}
          isFullPage={false}
          user={user}
        />
      )}

      {selectedMaintenanceForModal && (
        <MaintenanceLogView
          maintenanceLogId={selectedMaintenanceForModal.id}
          token={token}
          onClose={() => setSelectedMaintenanceForModal(null)}
          onNavigateToDeviceView={onNavigateToDeviceView}
          onNavigateToMaintenanceHistory={onNavigateToMaintenanceHistory}
        />
      )}
    </div>
  );
};

export default DepartmentRecentActivity;
